import type {TaxonomyCategory} from "../../../stores/TaxonomyCategoryProvider";
import {makeAutoObservable} from "mobx";
import type {ReactNode} from "react";
import React, {useMemo} from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "../../../stores";
import {PartReCategorizationHandler} from "../../../components/categorization/PartRecategorizationHandler";
import type {PopoverProps} from "@mui/material/Popover";

type OnSelect = (newCats: TaxonomyCategory) => void;

/**
 * Small controller to handle the opening and closing of a list categories
 */
export class CategoryMenuController {
    anchor: HTMLButtonElement | undefined
    anchorOrigin?: PopoverProps['anchorOrigin']
    transformOrigin?: PopoverProps['transformOrigin']
    disableScrollLock?: PopoverProps['disableScrollLock']

    private onSelect?: OnSelect

    constructor(public taxonomy: TaxonomyCategory[]) {
        makeAutoObservable(this, {
            setOnSelect: false,
            onSelectCategory: false,
        })
    }

    close() {
        this.anchor = undefined
    }

    open(
        anchor: HTMLButtonElement,
        anchorOrigin?: PopoverProps['anchorOrigin'],
        transformOrigin?: PopoverProps['transformOrigin'],
        disableScrollLock?: PopoverProps['disableScrollLock'],
    ) {
        this.anchor = anchor
        this.anchorOrigin = anchorOrigin ?? {vertical: 'bottom', horizontal: 'center'}
        this.transformOrigin = transformOrigin ?? {vertical: 'top', horizontal: 'center'}
        this.disableScrollLock = disableScrollLock
    }

    get isOpen() {
        return Boolean(this.anchor)
    }

    setOnSelect(onSelect: OnSelect) {
        this.onSelect = onSelect
    }

    onSelectCategory(c: TaxonomyCategory) {
        if (this.onSelect) {
            this.onSelect(c)
        }
        this.close()
    }
}

export const CategoryMenuControllerContext = React.createContext<CategoryMenuController | undefined>(undefined);

export const CategoryMenuProvider: React.FC<{children: ReactNode}> = observer(({children}) => {
    const {categorizationReviewStore} = useStores()
    const categories = categorizationReviewStore.categories;

    const controller = useMemo(() => {
        if (!categories.taxonomy_categories) {
            if (categories.taxonomy_categories === null) {
                console.error('No taxonomy set...')
            }
            return undefined
        }
        return new CategoryMenuController(categories.taxonomy_categories);
    }, [categories.taxonomy_categories])

    return <CategoryMenuControllerContext.Provider value={controller}>
        {controller &&
            <PartReCategorizationHandler controller={controller} label="Change the category"/>
        }
        {children}
    </CategoryMenuControllerContext.Provider>
})
